import { DndContext, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { memo, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useSelector } from "react-redux";
import { combineClasses } from "@/lib/utils";
import Pagination from "../Table/Pagination";
import TableSkeleton from "../Table/TableSkeleton";
import NoData from "@/components/SVGComponents/NoData";
import CustomHeader from "@/components/CustomHeader/CustomHeader";
import DragGripIcon from "@/components/SVGComponents/DragGripIcon";
import { renderAutoTooltipCell, shouldAutoTooltip } from "@/components/Tooltip";
import styles from "./DraggableQuestionTable.module.scss";
import type { RootState } from "@/store/rootReducer";
import type { Column, CustomHeaderProps } from "@/types/common";

export type InfiniteScrollProps = {
  enabled: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadingLabel?: string;
  onLoadMore?: () => void;
};

export interface DraggableQuestionTableProps<T extends { id: string | number }> {
  columns: Column<T>[];
  data: T[];
  className?: string;
  progressPending?: boolean;
  progressComponent?: ReactNode;
  paginationProps?: {
    isPagination: boolean;
    totalCount?: number;
    pageSelected?: number;
    rowsPerPageValue?: number;
    setPageSelected: (page: number) => void;
  };
  containerHeight?: string;
  infiniteScrollProps?: InfiniteScrollProps;
  // Return `false` to reject the new order — the row snaps back.
  onRowReorder?: (newData: T[]) => boolean | void;
  // Renders `CustomHeader` inside this table's own bordered card — the
  // same convention `Table`'s own `header` prop uses — instead of a page
  // assembling a standalone `CustomHeader` above the table separately.
  header?: CustomHeaderProps;
}

const getColumnStyle = (column: Column<any>) => {
  const style: Record<string, string> = { textAlign: column?.center ? "center" : "left" };
  if (column?.width) style.width = column.width;
  return style;
};

const SortableRow = <T extends { id: string | number }>({
  row,
  columns,
}: {
  row: T;
  columns: Column<T>[];
}) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
  });

  // `position`/`zIndex` are only set while actually dragging (to lift the
  // row above its neighbors). Setting an explicit `zIndex` unconditionally
  // — even 0 — makes every row its own stacking context, which then traps
  // a row's own descendants (e.g. RowActionsMenu's dropdown) below any
  // later row in the table, since z-index only resolves within the
  // nearest stacking context.
  const style = isDragging
    ? { transform: CSS.Transform.toString(transform), transition, position: "relative" as const, zIndex: 2 }
    : { transform: CSS.Transform.toString(transform), transition };

  return (
    <tr ref={setNodeRef} style={style} className={isDragging ? styles.dragging : ""}>
      {columns.map((col, index) => (
        <td
          key={index}
          style={getColumnStyle(col)}
          className={combineClasses(col.center && styles.centerCell, index === 0 && styles.dragCell, col.className)}
        >
          {index === 0 ? (
            <button
              type="button"
              ref={setActivatorNodeRef}
              {...attributes}
              {...listeners}
              className={styles.dragHandle}
              aria-label="Reorder row"
            >
              <DragGripIcon />
            </button>
          ) : col.cell ? (
            col.cell(row)
          ) : shouldAutoTooltip(col) ? (
            renderAutoTooltipCell(col.selector?.(row), { tooltip: col.tooltip, align: col.center ? "center" : undefined })
          ) : col.selector ? (
            col.selector(row)
          ) : null}
        </td>
      ))}
    </tr>
  );
};

const DraggableQuestionTable = <T extends { id: string | number }>(props: DraggableQuestionTableProps<T>) => {
  const { sidebarOpen } = useSelector((state: RootState) => state.global);
  const {
    columns = [],
    data = [],
    className,
    progressPending,
    progressComponent,
    paginationProps,
    containerHeight,
    infiniteScrollProps,
    onRowReorder,
    header,
  } = props;
  const [items, setItems] = useState<T[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreLockedRef = useRef(false);
  const wasLoadingMoreRef = useRef(false);
  const skeletonColumns = columns.map((col) => ({ name: String(col?.name ?? ""), ...(col?.width ? { width: col.width } : {}) }));

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 5 } }),
  );

  useEffect(() => {
    setItems(Array.isArray(data) ? data : []);
  }, [data]);

  useEffect(() => {
    if (!infiniteScrollProps?.enabled) {
      loadMoreLockedRef.current = false;
      wasLoadingMoreRef.current = false;
      return;
    }

    if (wasLoadingMoreRef.current && !infiniteScrollProps.isLoadingMore) {
      loadMoreLockedRef.current = false;
    }
    if (!infiniteScrollProps.hasMore) {
      loadMoreLockedRef.current = false;
    }

    wasLoadingMoreRef.current = Boolean(infiniteScrollProps.isLoadingMore);
  }, [infiniteScrollProps?.enabled, infiniteScrollProps?.hasMore, infiniteScrollProps?.isLoadingMore]);

  useEffect(() => {
    if (!infiniteScrollProps?.enabled || !infiniteScrollProps.onLoadMore) return;

    const hasMore = infiniteScrollProps.hasMore;
    const isLoadingMore = infiniteScrollProps.isLoadingMore;
    const onLoadMore = infiniteScrollProps.onLoadMore;
    const scrollContainer = scrollContainerRef.current;
    const loadMoreTrigger = loadMoreTriggerRef.current;

    if (!scrollContainer || !loadMoreTrigger) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting || !hasMore || isLoadingMore || loadMoreLockedRef.current) return;

        loadMoreLockedRef.current = true;
        onLoadMore();
      },
      { root: scrollContainer, rootMargin: "0px 0px 160px 0px", threshold: 0.1 },
    );

    observer.observe(loadMoreTrigger);
    return () => observer.disconnect();
  }, [
    infiniteScrollProps?.enabled,
    infiniteScrollProps?.hasMore,
    infiniteScrollProps?.isLoadingMore,
    infiniteScrollProps?.onLoadMore,
    items.length,
  ]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    if (onRowReorder?.(reordered) === false) setItems(items);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: containerHeight || "calc(100vh - 150px)",
        overflow: "hidden",
      }}
    >
      <div className={styles.tableWrapper}>
        {header && (
          <div className={styles.tableHeader}>
            <CustomHeader {...header} />
          </div>
        )}
        {progressPending ? (
          progressComponent || <TableSkeleton columns={skeletonColumns} sidebarOpen={sidebarOpen} />
        ) : (
          <div
            ref={scrollContainerRef}
            className={combineClasses("scrollable", styles.table, sidebarOpen && styles.sidebarOpen, className)}
          >
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                <table>
                  <colgroup>
                    {columns.map((col, index) => (
                      <col key={index} style={col?.width ? { width: col.width } : undefined} />
                    ))}
                  </colgroup>

                  <thead>
                    <tr>
                      {columns.map((col, index) => (
                        <th key={index} style={getColumnStyle(col)} className={index === 0 ? styles.dragHeaderCell : ""}>
                          {col.name}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {items.length ? (
                      items.map((row) => <SortableRow key={row.id} row={row} columns={columns} />)
                    ) : (
                      <tr className={styles.noDataRow}>
                        <td colSpan={columns.length}>
                          <div className={styles.noDataContent}>
                            <NoData />
                            <p className={styles.noData}>There are no records to display</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>

            {infiniteScrollProps?.enabled && (infiniteScrollProps.hasMore || infiniteScrollProps.isLoadingMore) ? (
              <div ref={loadMoreTriggerRef} className={styles.loadMoreTrigger} aria-hidden="true">
                {infiniteScrollProps.isLoadingMore ? (
                  <span className={styles.loadingMore}>{infiniteScrollProps.loadingLabel || "Loading more..."}</span>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {paginationProps?.isPagination && (
        <div className={styles.paginationWrapper}>
          <Pagination
            totalCount={paginationProps.totalCount}
            pageSelected={paginationProps.pageSelected}
            rowsPerPageValue={paginationProps.rowsPerPageValue}
            setPageSelected={paginationProps.setPageSelected}
          />
        </div>
      )}
    </div>
  );
};

export default memo(DraggableQuestionTable) as <T extends { id: string | number }>(
  props: DraggableQuestionTableProps<T>,
) => ReturnType<typeof DraggableQuestionTable>;
